import { useCallback, useState } from "react";
import { useClient, useMutation, useQuery } from "urql";
import type { StakeholderRelationsQuery as StakeholderRelationsQueryResult } from "../graphql/generated/graphql";
import {
  BulkCreateStakeholderRelationsMutation,
  DeleteStakeholderRelationMutation,
  StakeholderRelationsQuery,
  UnregisteredSponsorEmployeeEmailsQuery,
} from "../graphql/stakeholderSettings.graphql";
import { toUserFacingMessage } from "../lib/graphqlErrorMessage";

/** 関係性1件。スキーマとずれないよう生成型から導出する */
export type StakeholderRelation =
  StakeholderRelationsQueryResult["stakeholderRelations"][number];

export interface StakeholderRelationInput {
  recipientEmail: string;
  recipientName: string;
  respondentEmail: string;
  respondentName: string;
}

export interface BulkCreateStakeholderRelationsResult {
  createdRelationCount: number;
  createdSponsorEmployeeCount: number;
  createdRecipientCount: number;
  createdRespondentCount: number;
  /** 入稿と同時に作られたシートの件数 */
  createdSheetCount: number;
  /** シートを作れなかった関係性の件数。0 でなければ作成のやり直しが要る */
  failedSheetCount: number;
  sheetErrorMessage?: string | null;
}

export function useStakeholderRelations(termId: string | null) {
  const [result, reexecuteQuery] = useQuery({
    query: StakeholderRelationsQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  const refetch = useCallback(() => {
    reexecuteQuery({ requestPolicy: "network-only" });
  }, [reexecuteQuery]);

  return {
    stakeholderRelations: result.data?.stakeholderRelations ?? [],
    loading: result.fetching,
    error: result.error
      ? toUserFacingMessage(result.error, "関係性の取得に失敗しました")
      : null,
    refetch,
  };
}

export function useStakeholderRelationMutations() {
  const client = useClient();
  const [, executeBulkCreate] = useMutation(
    BulkCreateStakeholderRelationsMutation,
  );
  const [, executeDelete] = useMutation(DeleteStakeholderRelationMutation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  /** 入稿前のプレビュー用。未登録の従業員が何人かを知るために使う */
  const findUnregisteredEmails = useCallback(
    async (termId: string, emails: string[]): Promise<string[] | null> => {
      if (emails.length === 0) return [];
      setPreviewError(null);
      try {
        const response = await client
          .query(
            UnregisteredSponsorEmployeeEmailsQuery,
            { termId, emails },
            // 入稿で従業員が作られるとキャッシュ済みの結果は古くなる。
            // 何人増えるかを事前に知るための問い合わせなので、必ずサーバへ問い合わせる
            { requestPolicy: "network-only" },
          )
          .toPromise();
        if (response.error)
          throw new Error(
            toUserFacingMessage(
              response.error,
              "サーバーに接続できませんでした",
            ),
          );
        return response.data?.unregisteredSponsorEmployeeEmails ?? [];
      } catch (err) {
        setPreviewError(
          err instanceof Error
            ? err.message
            : "新しく登録される従業員数を確認できませんでした",
        );
        return null;
      }
    },
    [client],
  );

  const bulkCreateStakeholderRelations = useCallback(
    async (
      stakeholderSettingId: string,
      relations: StakeholderRelationInput[],
    ): Promise<BulkCreateStakeholderRelationsResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const response = await executeBulkCreate({
          input: { stakeholderSettingId, relations },
        });
        if (response.error)
          throw new Error(
            toUserFacingMessage(response.error, "入稿に失敗しました"),
          );
        return response.data?.bulkCreateStakeholderRelations ?? null;
      } catch (err) {
        setError(err instanceof Error ? err.message : "入稿に失敗しました");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeBulkCreate],
  );

  const deleteStakeholderRelation = useCallback(
    async (stakeholderRelationId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const response = await executeDelete({ stakeholderRelationId });
        if (response.error)
          throw new Error(
            toUserFacingMessage(response.error, "削除に失敗しました"),
          );
        return response.data?.deleteStakeholderRelation ?? false;
      } catch (err) {
        setError(err instanceof Error ? err.message : "削除に失敗しました");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeDelete],
  );

  return {
    findUnregisteredEmails,
    bulkCreateStakeholderRelations,
    deleteStakeholderRelation,
    loading,
    error,
    previewError,
  };
}
